import conversationSocketService from '@/services/socket/socketCoordinator.service.js'
import { toRaw } from 'vue'
import { handleNewMessage } from '@/stores/helpers/messageHandlerUtils.js'
import messageService from '@/services/api/message.service.js'

//db utils
import { putMessages } from '@/utils/db/messages.js'
import {
    addConversation,
    updateConversationLastMessage,
} from '@/utils/db/conversations.js'
import {
    markMessages,
    updateMessageModerationStatus,
    updateMessageTranscript,
} from '@/utils/db/messages.js'
import { putUsers, updateUserOnlineStatus } from '@/utils/db/users.js'

// Store instances - will be set by initializeConversationSocketHandlers
let convStore = null
let auth = null

async function handleMessageCreditsUpdatedHandler(data) {
    try {
        console.log('ConvSocketManager: message_credits_updated event received', data)
        const conversationId = data?.conversationId
        const remaining = data?.remaining
        if (!conversationId || !remaining) {
            console.warn('ConvSocketManager: Invalid credits update data', data)
            return
        }

        // Update in-memory store list
        if (convStore?.state?.conversations?.length) {
            const idx = convStore.state.conversations.findIndex(
                (c) => c._id === conversationId
            )
            if (idx > -1) {
                console.log(`ConvSocketManager: Updating credits for conversation ${conversationId} in list`)
                convStore.state.conversations[idx] = {
                    ...convStore.state.conversations[idx],
                    paidMessaging: {
                        ...(convStore.state.conversations[idx].paidMessaging || {}),
                        remaining,
                    },
                }
            }
        }

        // Update currentConversation if active
        // Use loose equality to handle potential type mismatches (string vs ObjectId)
        if (convStore?.state?.currentConversation?._id == conversationId) {
            console.log(`ConvSocketManager: Updating credits for currentConversation ${conversationId}`)
            convStore.state.currentConversation = {
                ...convStore.state.currentConversation,
                paidMessaging: {
                    ...(convStore.state.currentConversation.paidMessaging || {}),
                    remaining,
                },
            }
        } else {
             console.log(`ConvSocketManager: Not updating currentConversation. ID mismatch: Current '${convStore?.state?.currentConversation?._id}' vs Update '${conversationId}'`)
        }

        // Persist to IndexedDB (use toRaw to convert Proxy to plain object)
        try {
            const { updateConversation } = await import('@/utils/db/conversations')
            const toPersist =
                convStore?.state?.currentConversation?._id == conversationId
                    ? toRaw(convStore.state.currentConversation)
                    : toRaw(
                          convStore.state.conversations.find(
                              (c) => c._id === conversationId
                          )
                      )
            if (toPersist) {
                await updateConversation(toPersist)
                console.log('ConvSocketManager: Credits update persisted to DB')
            }
        } catch (err) {
            console.error(
                'ConvSocketManager: Failed to persist message credits update to DB',
                err
            )
        }
    } catch (err) {
        console.error('ConvSocketManager: Error handling credits update', err)
    }
}

// Handler functions (previously in conversationSocketHandlers.js)
// They will now use convStore auth defined above.

async function handleNewMessageHandler(message) {
    const isSender = auth.user?.data?._id === message.sender
    console.log(
        'ConversationSocketManager: New message received',
        message,
        'Is sender:',
        isSender
    )

    const state = convStore.state
    if (message?.conversationId) {
        convStore.clearCopilotSuggestions?.(message.conversationId)
    }

    // Clear typing indicator when receiving a message from the other party
    // This handles the race condition where "stopTyping" event arrives after the message
    if (
        !isSender &&
        message.conversationId === state.currentConversation?._id
    ) {
        state.currentReceiverWriting = false
    }

    // The new helper needs a db object.
    const db = {
        putMessages,
        updateConversationLastMessage,
    }

    await handleNewMessage(state, db, message, isSender)
}

async function handleMessageStatusUpdateHandler(payload) {
    const { messageIds, userId, statusType } = payload
    console.log(
        `ConversationSocketManager: Status update '${statusType}' for messages by user ${userId}`,
        messageIds
    )

    if (!Array.isArray(messageIds) || messageIds.length === 0 || !userId) {
        console.warn(
            'ConvSocketManager: Invalid data for message status update:',
            payload
        )
        return
    }

    try {
        await markMessages(messageIds, userId, { [statusType]: true })
    } catch (err) {
        console.error(
            'DB Error (ConvSocketManager): Failed to mark messages -',
            err
        )
    }

    const state = convStore.state
    const statusArrayField = `${statusType}By`

    // Update current messages if applicable
    if (state.currentConversation?._id) {
        let changedInCurrent = 0
        state.currentMessages = state.currentMessages.map((msg) => {
            if (
                messageIds.includes(msg._id) &&
                msg.conversationId === state.currentConversation._id
            ) {
                if (!msg[statusArrayField]?.includes(userId)) {
                    changedInCurrent++
                    return {
                        ...msg,
                        [statusArrayField]: [
                            ...(msg[statusArrayField] || []),
                            userId,
                        ],
                    }
                }
            }
            return msg
        })
        if (changedInCurrent > 0) {
            console.log(
                `ConvSocketManager: Updated Pinia currentMessages for ${changedInCurrent} ${statusType} messages.`
            )
        }
    }

    // Update lastMessage in conversations list
    let changedInConvoList = 0
    state.conversations.forEach((convo) => {
        if (convo.lastMessage && messageIds.includes(convo.lastMessage._id)) {
            if (!convo.lastMessage[statusArrayField]?.includes(userId)) {
                // Vue should detect this change if lastMessage is reactive
                convo.lastMessage[statusArrayField] = [
                    ...(convo.lastMessage[statusArrayField] || []),
                    userId,
                ]
                changedInConvoList++
            }
        }
    })
    if (changedInConvoList > 0) {
        console.log(
            `ConvSocketManager: Updated Pinia conversations list (lastMessage) for ${changedInConvoList} ${statusType} messages.`
        )
    }
}

async function handleNewConversationHandler(conversationMessage) {
    const { conversation, sender } = conversationMessage
    console.log(
        'ConversationSocketManager: New conversation received',
        conversation
    )

    const state = convStore.state
    try {
        await addConversation(conversation)
        if (sender && sender._id) {
            // Ensure sender is valid
            await putUsers([sender])
            state.usersMap[sender._id] = sender // Add/update user in Pinia's map
        }

        if (!state.conversations.some((c) => c._id === conversation._id)) {
            state.conversations.push(conversation)
            console.log(
                'ConvSocketManager: Added new conversation to Pinia state.'
            )
        }

        if (auth.user?.data) {
            // Using the main conversationSocketService here
            await conversationSocketService.joinNewConversation(
                conversation._id,
                auth.user.data
            )
            console.log(
                `ConvSocketManager: Joined new conversation room ${conversation._id}.`
            )
        }
    } catch (error) {
        console.error(
            `ConvSocketManager: Error handling new conversation ${conversation._id} -`,
            error
        )
    }
}

function handleTypingMessageHandler(typingData) {
    const { senderId, conversationId } = typingData
    const state = convStore.state
    if (
        senderId !== auth.user?.data?._id &&
        conversationId === state.currentConversation?._id
    ) {
        state.currentReceiverWriting = true
    }
}

function handleStopTypingMessageHandler(stopTypingData) {
    const { conversationId } = stopTypingData
    const state = convStore.state
    if (conversationId === state.currentConversation?._id) {
        state.currentReceiverWriting = false
    }
}

async function handleStatusChangedHandler(statusUpdate) {
    const { userId, status, last_seen } = statusUpdate
    console.log(
        `ConversationSocketManager: User status changed for ${userId}: ${status}`
    )
    const state = convStore.state
    try {
        await updateUserOnlineStatus(userId, status, last_seen)
    } catch (err) {
        console.error(
            `DB Error (ConvSocketManager): Failed to update user ${userId} online status -`,
            err
        )
    }

    if (state.usersMap && state.usersMap[userId]) {
        state.usersMap[userId].onlineStatus = { status, last_seen }
    } else if (state.usersMap) {
        // usersMap exists but user not in it
        console.log(
            `ConvSocketManager: User ${userId} not in usersMap for status update. (Potentially fetch user details).`
        )
        // state.usersMap[userId] = { _id: userId, onlineStatus: { status, last_seen } }; // Or fetch full user
    }
}

async function handleUserWalletUpdatedHandler(payload) {
    const { userId, wallet } = payload
    console.log(
        `ConversationSocketManager: Wallet updated for user ${userId}`,
        wallet
    )
    const state = convStore.state

    if (state.usersMap && state.usersMap[userId]) {
        if (!state.usersMap[userId].wallet) {
            state.usersMap[userId].wallet = {}
        }
        state.usersMap[userId].wallet.balance = wallet.balance
        console.log(
            `ConvSocketManager: Updated wallet balance for user ${userId} in usersMap.`
        )
    }
}

async function handleMessageModerationStatusUpdatedHandler(moderationUpdate) {
    const { messageId, moderationStatus } = moderationUpdate
    console.log(
        `ConversationSocketManager: Moderation status for message ${messageId} updated to ${moderationStatus}`
    )
    const state = convStore.state

    try {
        await updateMessageModerationStatus(messageId, moderationStatus)
    } catch (err) {
        console.error(
            `DB Error (ConvSocketManager): Failed to update message ${messageId} moderation status -`,
            err
        )
    }

    let updatedInCurrent = false
    if (state.currentConversation?._id) {
        state.currentMessages = state.currentMessages.map((msg) => {
            if (
                msg._id === messageId &&
                msg.conversationId === state.currentConversation._id
            ) {
                updatedInCurrent = true
                return { ...msg, moderationStatus }
            }
            return msg
        })
        if (updatedInCurrent)
            console.log(
                `ConvSocketManager: Updated moderation status for message ${messageId} in currentConversation.`
            )

        // Update lastMessage in currentConversation if it's the moderated one
        if (state.currentConversation.lastMessage?._id === messageId) {
            state.currentConversation.lastMessage.moderationStatus =
                moderationStatus
        }
    }

    // Update lastMessage in the conversations list
    const convoToUpdate = state.conversations.find(
        (c) => c.lastMessage?._id === messageId
    )
    if (convoToUpdate && convoToUpdate.lastMessage) {
        convoToUpdate.lastMessage.moderationStatus = moderationStatus
        console.log(
            `ConvSocketManager: Updated moderation status for lastMessage in conversation list (convo ${convoToUpdate._id}).`
        )
    }
    // Note: updateConversationLastMessage(message.conversationId, message) might be better if the full message object with updated moderation is available.
    // For now, just updating the specific field in Pinia state. The DB was updated directly above.
}

async function handleCreatorUpdatedHandler(payload) {
    const { userId, ...updateData } = payload
    console.log(
        `ConversationSocketManager: Creator update received for ${userId}`,
        updateData
    )
    const state = convStore.state

    if (state.usersMap && state.usersMap[userId]) {

        // IMPORTANT: We must use toRaw() on the existing user object because accessing state.usersMap[userId]
        // returns a Proxy. Spreading a Proxy propagates nested Proxies to the new object's properties.
        // Dexie/IndexedDB cannot clone Proxies (DataCloneError). toRaw() gives us the plain underlying object.
        const rawUser = toRaw(state.usersMap[userId])
        
        const updatedUser = {
            ...rawUser,
            ...updateData,
        }

        state.usersMap[userId] = updatedUser
        
        // Also update IndexedDB for persistence
        try {
            await putUsers([updatedUser])
            console.log(`ConvSocketManager: Persisted creator update for ${userId} to DB.`)
        } catch (err) {
            console.error(`ConvSocketManager: Failed to persist creator update for ${userId}`, err)
        }
    } else {
        console.log(
            `ConvSocketManager: Creator ${userId} not in usersMap, skipping update.`
        )
    }
}

async function handleTranscriptUpdatedHandler(data) {
    const { messageId, transcript, transcriptLanguage, transcriptionStatus, translations } = data
    console.log(
        `ConversationSocketManager: Transcript updated for message ${messageId}: status=${transcriptionStatus}`
    )
    const state = convStore.state

    // Update in IndexedDB (including translations if present)
    try {
        await updateMessageTranscript(messageId, transcript, transcriptLanguage, transcriptionStatus, translations)
    } catch (err) {
        console.error(
            `DB Error (ConvSocketManager): Failed to update message ${messageId} transcript -`,
            err
        )
    }

    // Update in current messages if applicable
    let updatedInCurrent = false
    if (state.currentConversation?._id) {
        state.currentMessages = state.currentMessages.map((msg) => {
            if (
                msg._id === messageId &&
                msg.conversationId === state.currentConversation._id
            ) {
                updatedInCurrent = true
                const updatedMsg = {
                    ...msg,
                    transcript,
                    transcriptLanguage,
                    transcriptionStatus,
                }
                // Merge translations if present
                if (translations && Object.keys(translations).length > 0) {
                    updatedMsg.translations = {
                        ...(msg.translations || {}),
                        ...translations,
                    }
                }
                return updatedMsg
            }
            return msg
        })
        if (updatedInCurrent)
            console.log(
                `ConvSocketManager: Updated transcript for message ${messageId} in currentConversation.`
            )
    }

    // Update lastMessage in conversations list if it's the transcribed one
    const convoToUpdate = state.conversations.find(
        (c) => c.lastMessage?._id === messageId
    )
    if (convoToUpdate && convoToUpdate.lastMessage) {
        convoToUpdate.lastMessage.transcript = transcript
        convoToUpdate.lastMessage.transcriptLanguage = transcriptLanguage
        convoToUpdate.lastMessage.transcriptionStatus = transcriptionStatus
        // Merge translations if present
        if (translations && Object.keys(translations).length > 0) {
            convoToUpdate.lastMessage.translations = {
                ...(convoToUpdate.lastMessage.translations || {}),
                ...translations,
            }
        }
        console.log(
            `ConvSocketManager: Updated transcript for lastMessage in conversation list (convo ${convoToUpdate._id}).`
        )
    }
}

/**
 * Initializes socket listeners for conversation-related events.
 * This function will be called from the conversation store.
 *
 * @param {Object} store - The Pinia conversation store instance.
 * @param {Object} authStore - The Pinia auth store instance.
 */
export function initializeConversationSocketHandlers(store, authStoreInstance) {
    convStore = store
    auth = authStoreInstance

    if (
        !conversationSocketService ||
        typeof conversationSocketService.setupSocketListeners !== 'function'
    ) {
        console.error(
            'ConvSocketManager: conversationSocketService or its setupSocketListeners method is not available.'
        )
        return
    }

    conversationSocketService.setupSocketListeners({
        // These are the event names the main socket.service.js listens for and their corresponding handlers
        'new message': handleNewMessageHandler,
        'messages received': (data) =>
            handleMessageStatusUpdateHandler({
                ...data,
                statusType: 'received',
            }), // data = { messageIds, userId }
        'messages read': (data) =>
            handleMessageStatusUpdateHandler({ ...data, statusType: 'read' }), // data = { messageIds, userId }
        'new conversation': handleNewConversationHandler, // data = { conversation, sender }
        'typing message': handleTypingMessageHandler, // data = { senderId, conversationId }
        'stop typing message': handleStopTypingMessageHandler, // data = { conversationId } - or could be same as typing
        user_status_update: handleStatusChangedHandler, // data = { userId, status, last_seen }
        user_wallet_updated: handleUserWalletUpdatedHandler, // data = { userId, wallet }

        message_moderation_status_updated:
            handleMessageModerationStatusUpdatedHandler, // data = { messageId, moderationStatus }
        creator_updated: handleCreatorUpdatedHandler, // data = { userId, ...updateData }

        message_credits_updated: handleMessageCreditsUpdatedHandler, // data = { conversationId, remaining, ... }
        
        transcript_updated: handleTranscriptUpdatedHandler, // data = { messageId, transcript, transcriptLanguage, transcriptionStatus }
    })

    console.log(
        'ConversationSocketManager: Conversation socket listeners setup complete.'
    )
}

// No explicit cleanup function here as socket.service.js's cleanupSockets should handle these
// events if they were registered using its `registeredEventNames` set.
// If this manager had its own unique events not through socket.service's main registration,
// a cleanup specific to this manager would be needed.

export default {
    initializeConversationSocketHandlers,
}
